/**
 * Utility functions for the BlockBuilder library
 */

/**
 * Validates that a value is not null or undefined
 * 
 * @param value - Value to check
 * @param name - Name of the parameter for error message
 */
export function validateRequired<T>(value: T | null | undefined, name: string): T {
  if (value === null || value === undefined) {
    throw new Error(`${name} is required`);
  }
  return value;
}

/**
 * Validates that a string is not empty
 * 
 * @param value - String to check
 * @param name - Name of the parameter for error message
 */
export function validateNonEmptyString(value: string | null | undefined, name: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${name} must be a non-empty string`);
  }
  return value;
}

/**
 * Validates that a number is within a range
 * 
 * @param value - Number to check
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @param name - Name of the parameter for error message
 */
export function validateRange(value: number, min: number, max: number, name: string): number {
  if (typeof value !== 'number' || value < min || value > max) {
    throw new Error(`${name} must be a number between ${min} and ${max}`);
  }
  return value;
}

/**
 * Deep clones an object
 * 
 * @param obj - Object to clone
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Merges two objects deeply
 * 
 * @param target - Target object
 * @param source - Source object
 */
export function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const output = { ...target };
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  
  return output;
}

/**
 * Checks if a value is an object
 * 
 * @param item - Value to check
 */
function isObject(item: any): item is object {
  return item && typeof item === 'object' && !Array.isArray(item);
}
